import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Plus, ArrowRight, Clock, CheckCircle, AlertCircle, 
  MessageCircle, Sparkles, TrendingUp, CreditCard, Package,
  Video, Wand2, BookOpen, ChevronRight, Phone, Award, 
  Circle, CheckCircle2, Gift, Rocket, Trophy, ClipboardList,
  Bookmark, Users, Send, Globe, Star, Heart, Tag, Share2, AtSign, GraduationCap,
  Film, Clapperboard, Play, X, Loader2, MapPin,
  ExternalLink, DollarSign, BarChart3
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { api, useAuth } from '../lib/auth';
import { WorkspaceLaunchCard } from '../components/WorkspaceLaunchCard';
import { formatCurrency, getStatusColor, getStatusLabel, formatDate, formatAIResponse } from '../lib/utils';
import { toast } from 'sonner';
import { AthenaVoiceChat } from '../components/AthenaVoiceChat';
import { MemberAnalyticsDashboard } from '../components/social';
import { cn } from '../lib/utils';

// External Directory URL
const EXTERNAL_DIRECTORY_URL = process.env.REACT_APP_EXTERNAL_DIRECTORY_URL || 'https://search.aihorsepowered.com';

// Success Journey Steps for Dashboard Checklist (Business users)
const SUCCESS_JOURNEY_STEPS = [
  { key: 'account', label: 'Start Your Free Trial', icon: CheckCircle2, description: '30 days to explore everything!' },
  { key: 'coop', label: 'Get Co-Op Funding', icon: Gift, description: '$120k (4% Fee or 5% Easy Pay)' },
  { key: 'onboarding', label: 'Complete Onboarding', icon: ClipboardList, description: 'Brand breakdown & setup' },
  { key: 'certified', label: 'Get AI Certified', icon: Award, description: '"Voted Best of Best" badge' },
  { key: 'first_order', label: 'Place Your First Order', icon: Package, description: 'Start getting projects done' },
  { key: 'success', label: 'Achieve Success!', icon: Trophy, description: 'Your Workspace works for you daily' },
];

// Quick Links configuration for Business users
const BUSINESS_QUICK_LINKS = [
  { 
    path: '/agent', 
    label: 'AGI Workhorse', 
    description: 'Task Me Anything',
    icon: Sparkles, 
    color: 'cyan',
    gradient: 'from-cyan-500/20 to-blue-500/20'
  },
  { 
    path: '/diy-tools', 
    label: 'Do It Yourself', 
    description: '3000+ AI tools at your fingertips',
    icon: Wand2, 
    color: 'purple',
    gradient: 'from-purple-500/20 to-pink-500/20'
  },
  { 
    path: '/knowledge', 
    label: 'AI Knowledge Base', 
    description: 'Ask Me Anything',
    icon: BookOpen, 
    color: 'green',
    gradient: 'from-green-500/20 to-emerald-500/20'
  },
  { 
    path: '/publishing', 
    label: 'Publish Content', 
    description: 'Share to your directory listing',
    icon: Send, 
    color: 'primary',
    gradient: 'from-primary/20 to-blue-500/20'
  },
  { 
    path: '/dashboard-leads', 
    label: 'My Directory Leads', 
    description: 'View leads from your listing',
    icon: Users, 
    color: 'teal',
    gradient: 'from-teal-500/20 to-cyan-500/20'
  },
  { 
    path: '/orders', 
    label: 'My Orders', 
    description: 'Track your service orders',
    icon: Package, 
    color: 'amber',
    gradient: 'from-amber-500/20 to-orange-500/20'
  },
];

// Quick Links configuration for Consumer users
const CONSUMER_QUICK_LINKS = [
  { 
    path: '/directory', 
    label: 'Browse Directory', 
    description: 'Discover local businesses & services',
    icon: Globe, 
    color: 'primary',
    gradient: 'from-cyan-500/20 to-teal-500/20'
  },
  { 
    path: '/bookmarks', 
    label: 'My Bookmarks', 
    description: 'View your saved businesses',
    icon: Bookmark, 
    color: 'amber',
    gradient: 'from-amber-500/20 to-orange-500/20'
  },
  { 
    path: '/messages', 
    label: 'Messages', 
    description: 'Chat with businesses & community',
    icon: MessageCircle, 
    color: 'purple',
    gradient: 'from-purple-500/20 to-pink-500/20'
  },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [brandProfile, setBrandProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatMessage, setChatMessage] = useState('');
  const [chatResponse, setChatResponse] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [showVoiceChat, setShowVoiceChat] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [messageCount, setMessageCount] = useState(0);

  // Determine if user is a consumer
  const isConsumer = user?.role === 'consumer';
  const QUICK_LINKS = isConsumer ? CONSUMER_QUICK_LINKS : BUSINESS_QUICK_LINKS;

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadData = async () => {
    try {
      if (isConsumer) {
        // Consumer data loading - bookmarks, messages
        const [orgRes, bookmarksRes] = await Promise.all([
          api.getMyOrganization(),
          api.get('/api/social/bookmarks').catch(() => ({ data: [] }))
        ]);
        setOrganization(orgRes.data);
        setBookmarks(bookmarksRes.data?.slice(0, 5) || []);
      } else {
        // Business user data loading - include expert sessions
        const [ordersRes, orgRes, profileRes, sessionsRes] = await Promise.all([
          api.getOrders(),
          api.getMyOrganization(),
          api.getBrandProfile().catch(() => ({ data: null })),
          api.getZoomRequests().catch(() => ({ data: [] }))  // Get DWY sessions
        ]);
        
        // Combine orders with upcoming DWY sessions for display
        const upcomingOrders = ordersRes.data.slice(0, 5);
        const upcomingSessions = (sessionsRes.data || [])
          .filter(s => s.status !== 'cancelled' && s.status !== 'completed')
          .slice(0, 3)
          .map(s => ({
            ...s,
            isDWYSession: true,
            service_title: `DWY Session: ${s.topic}`,
            department_name: s.department_name || 'Expert Session',
            created_at: s.created_at
          }));
        
        setOrders([...upcomingSessions, ...upcomingOrders].slice(0, 5));
        setOrganization(orgRes.data);
        setBrandProfile(profileRes.data);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate journey completion
  const getJourneyStatus = () => {
    const hasCoopCredits = organization?.coop_balance > 0;
    const isOnboarded = brandProfile?.identity_complete && brandProfile?.voice_complete && brandProfile?.compliance_complete;
    const isCertified = brandProfile?.ai_certification_acknowledged;
    const hasOrders = orders.length > 0;
    const hasCompletedOrder = orders.some(o => o.status === 'completed');

    return {
      account: true, // Always true if they're on dashboard
      coop: hasCoopCredits,
      onboarding: isOnboarded,
      certified: isCertified,
      first_order: hasOrders,
      success: hasCompletedOrder
    };
  };

  const journeyStatus = getJourneyStatus();
  const completedSteps = Object.values(journeyStatus).filter(Boolean).length;
  const journeyProgress = Math.round((completedSteps / SUCCESS_JOURNEY_STEPS.length) * 100);

  const handleQuickChat = async () => {
    if (!chatMessage.trim()) return;
    
    setChatLoading(true);
    try {
      const response = await api.sendChatMessage({ message: chatMessage });
      setChatResponse(response.data.response);
    } catch (error) {
      toast.error('Failed to get response');
    } finally {
      setChatLoading(false);
    }
  };

  const activeOrders = orders.filter(o => !['completed', 'cancelled'].includes(o.status));
  const completedCount = orders.filter(o => o.status === 'completed').length;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* AI Horse Hero Section with Waving GIF */}
        {!isConsumer && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="relative mb-4 min-h-[200px] md:min-h-[280px] flex flex-col md:flex-row items-start md:items-center pt-4"
          >
            {/* AI Horse Waving GIF - Desktop: absolute right */}
            <div className="hidden md:block absolute right-0 top-12 w-[450px] h-[450px] pointer-events-none z-0">
              <div 
                className="absolute inset-0"
                style={{
                  background: 'radial-gradient(ellipse at center, transparent 30%, #0a0a0a 70%)',
                }}
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_6e360c3b-e6a7-43c8-9e07-36a091b1723b/artifacts/bgqr5jdn_Untitled%20%289%20x%2014%20in%29%20%28900%20x%201100%20px%29%20%282%29.gif" 
                alt="AI Horse" 
                className="w-full h-full object-contain object-right-bottom"
                style={{
                  maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 75%)',
                  WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 75%)',
                }}
              />
            </div>
            
            {/* Welcome Header - overlaid on left */}
            <div className="relative z-10 max-w-2xl w-full">
              <h1 className="font-heading text-5xl sm:text-6xl font-bold text-white mb-3">
                Welcome, {user?.name?.split(' ')[0]}
              </h1>
              <p className="text-gray-400 text-lg">Here&apos;s what&apos;s happening with your missions.</p>
              {organization?.coop_balance > 0 && (
                <p className="text-primary mt-3 font-medium text-lg">
                  You have <span className="text-white font-bold">${organization.coop_balance.toLocaleString()}</span> in Co-Op Funds to Spend on Orders!
                </p>
              )}
            </div>
            
            {/* Mobile AI Horse - Larger, positioned waist-high above Quick Links cards */}
            <div className="md:hidden absolute right-[-20px] top-[60px] w-[280px] h-[280px] pointer-events-none z-0">
              <div 
                className="absolute inset-0"
                style={{
                  background: 'radial-gradient(ellipse at center, transparent 30%, #0a0a0a 70%)',
                }}
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_6e360c3b-e6a7-43c8-9e07-36a091b1723b/artifacts/bgqr5jdn_Untitled%20%289%20x%2014%20in%29%20%28900%20x%201100%20px%29%20%282%29.gif" 
                alt="AI Horse" 
                className="w-full h-full object-contain"
                style={{
                  maskImage: 'radial-gradient(ellipse at center, black 50%, transparent 80%)',
                  WebkitMaskImage: 'radial-gradient(ellipse at center, black 50%, transparent 80%)',
                }}
              />
            </div>
          </motion.div>
        )}

        {/* Consumer Welcome Header */}
        {isConsumer && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h1 className="font-heading text-5xl sm:text-6xl font-bold text-white mb-3">
              Welcome, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-gray-400 text-lg">Explore local businesses, manage your bookmarks, and connect with the community.</p>
          </motion.div>
        )}

        {/* Quick Links - Main Services */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
          data-tour="quick-links"
        >
          <h2 className="font-heading text-lg font-semibold text-white mb-4">Quick Links</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Open My Workspace - Slack */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
            >
              <WorkspaceLaunchCard compact />
            </motion.div>
            {QUICK_LINKS.map((link, i) => {
              const Icon = link.icon;
              return (
                <Link key={link.path} to={link.path} data-testid={`quick-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.05 }}
                    className={`glass rounded-2xl p-5 hover:border-${link.color}/30 transition-all cursor-pointer group h-full relative overflow-hidden`}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${link.gradient} opacity-0 group-hover:opacity-100 transition-opacity`} />
                    <div className="relative z-10">
                      <div className={`w-12 h-12 rounded-xl bg-${link.color}/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                        <Icon className={`w-6 h-6 text-${link.color}`} />
                      </div>
                      <h3 className="font-semibold text-white mb-1 group-hover:text-primary transition-colors">{link.label}</h3>
                      <p className="text-sm text-gray-400">{link.description}</p>
                      <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-primary absolute top-5 right-5 group-hover:translate-x-1 transition-all" />
                    </div>
                  </motion.div>
                </Link>
              );
            })}
          </div>
        </motion.div>

        {/* Cinematic Ad System Banner - Business Users Only */}
        {!isConsumer && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8"
            data-tour="cinematic-ads"
          >
            <Link to="/cinematic-ads">
              <div className="glass rounded-2xl p-5 border border-cyan-500/30 hover:border-cyan-500/50 transition-all cursor-pointer group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/5 to-pink-500/10 opacity-50" />
                <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                      <Clapperboard className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-heading text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors">
                          Cinematic Video Ad System
                        </h3>
                        <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">NEW</span>
                      </div>
                      <p className="text-sm text-gray-400">
                        Create stunning video ads with headline, copy, CTA & landing page — deploy to Directory, Meta, Google & TikTok
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="hidden sm:flex gap-2">
                      {['Video Ads', 'Reels', 'TV', 'Radio', 'Billboards'].map((type) => (
                        <span key={type} className="px-2 py-1 bg-white/5 text-gray-400 text-xs rounded-lg">{type}</span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-500/20 text-cyan-400 font-medium group-hover:bg-cyan-500/30 transition-all">
                      <Play className="w-4 h-4" />
                      Open Studio
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        )}

        {/* Stats Row - Different for Consumer vs Business */}
        {isConsumer ? (
          // Consumer Stats Row
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid sm:grid-cols-3 gap-4 mb-8"
          >
            <Link to="/bookmarks" data-testid="view-bookmarks-btn">
              <div className="glass rounded-2xl p-5 hover:border-amber-500/30 transition-colors cursor-pointer group h-full">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Saved Businesses</p>
                    <p className="text-2xl font-heading font-bold text-white">{bookmarks.length}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Bookmark className="w-6 h-6 text-amber-400" />
                  </div>
                </div>
              </div>
            </Link>
            
            <Link to="/messages" data-testid="view-messages-btn">
              <div className="glass rounded-2xl p-5 hover:border-purple-500/30 transition-colors cursor-pointer group h-full">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Messages</p>
                    <p className="text-2xl font-heading font-bold text-white">View</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-purple-400" />
                  </div>
                </div>
              </div>
            </Link>
            
            <a href={EXTERNAL_DIRECTORY_URL} target="_blank" rel="noopener noreferrer">
              <div className="glass rounded-2xl p-5 hover:border-cyan-500/30 transition-colors cursor-pointer group h-full">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Explore</p>
                    <p className="text-2xl font-heading font-bold text-white">Directory</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                    <Globe className="w-6 h-6 text-cyan-400" />
                  </div>
                </div>
              </div>
            </a>
          </motion.div>
        ) : (
          // Business Stats Row
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid sm:grid-cols-3 gap-4 mb-8"
          >
            <Link to="/orders" data-testid="view-orders-btn">
              <div className="glass rounded-2xl p-5 hover:border-primary/30 transition-colors cursor-pointer group h-full">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Active Orders</p>
                    <p className="text-2xl font-heading font-bold text-white">{activeOrders.length}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-400" />
                  </div>
                </div>
              </div>
            </Link>
            
            <div className="glass rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Completed</p>
                  <p className="text-2xl font-heading font-bold text-white">{completedCount}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </div>
            
            <Link to="/billing" data-tour="co-op-balance">
              <div className="glass rounded-2xl p-5 hover:border-primary/30 transition-colors cursor-pointer group h-full">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Co-Op Balance</p>
                    <p className="text-2xl font-heading font-bold text-white">
                      {formatCurrency(organization?.coop_balance || 0)}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-purple-400" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        )}

        {/* AI Certified Badge (if certified) - Business only */}
        {!isConsumer && brandProfile?.ai_certification_acknowledged && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.25 }}
            className="mb-8"
          >
            <div className="glass rounded-2xl p-6 border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-cyan-500/10">
              <div className="flex items-center gap-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ai-support-3/artifacts/5blyahh8_Black%20and%20White%20Business%20%20Question%20Facebook%20Post%20%28940%20x%20940%20px%29%20%281%29.png"
                  alt="AI Certified Badge"
                  className="w-24 h-24 object-contain"
                />
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold text-white mb-1">
                    🎉 Congratulations! You're AI Certified
                  </h3>
                  <p className="text-gray-400 text-sm mb-2">
                    {organization?.name || user?.name}'s business is now a "Voted Best of Best" AI Certified Business!
                  </p>
                  <p className="text-xs text-amber-400">
                    Eligible for press releases, industry listings, and trust badge display
                  </p>
                </div>
                <Link to="/onboarding">
                  <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10" data-testid="view-certification-btn">
                    View Certificate
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* Success Journey Checklist - Business only */}
        {!isConsumer && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-8"
        >
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Rocket className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-semibold text-white">Your Success Journey</h2>
                  <p className="text-sm text-gray-400">{completedSteps} of {SUCCESS_JOURNEY_STEPS.length} steps completed</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-primary">{journeyProgress}%</p>
                <p className="text-xs text-gray-500">Progress</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="h-2 bg-white/10 rounded-full mb-6 overflow-hidden">
              <motion.div 
                className="h-full bg-gradient-to-r from-primary to-cyan-400"
                initial={{ width: 0 }}
                animate={{ width: `${journeyProgress}%` }}
                transition={{ duration: 0.5, delay: 0.3 }}
              />
            </div>

            {/* Journey Steps */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {SUCCESS_JOURNEY_STEPS.map((step, index) => {
                const Icon = step.icon;
                const isComplete = journeyStatus[step.key];
                const isNext = !isComplete && Object.values(journeyStatus).slice(0, index).every(Boolean);
                
                return (
                  <div 
                    key={step.key}
                    className={`p-3 rounded-xl border transition-all ${
                      isComplete 
                        ? 'bg-green-500/10 border-green-500/30' 
                        : isNext 
                          ? 'bg-primary/10 border-primary/30 animate-pulse' 
                          : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isComplete ? 'bg-green-500/20' : isNext ? 'bg-primary/20' : 'bg-white/10'
                      }`}>
                        {isComplete ? (
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                        ) : (
                          <Icon className={`w-4 h-4 ${isNext ? 'text-primary' : 'text-gray-500'}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isComplete ? 'text-green-400' : isNext ? 'text-white' : 'text-gray-400'}`}>
                          {step.label}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{step.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Next Action CTA */}
            {!journeyStatus.success && (
              <div className="mt-4 pt-4 border-t border-white/10">
                {!journeyStatus.onboarding ? (
                  <Link to="/onboarding">
                    <Button className="w-full btn-primary" data-testid="journey-next-action">
                      Continue: Complete Onboarding <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                ) : !journeyStatus.certified ? (
                  <Link to="/onboarding">
                    <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black" data-testid="journey-next-action">
                      Next: Get AI Certified <Award className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                ) : !journeyStatus.first_order ? (
                  <Link to="/agent">
                    <Button className="w-full btn-primary" data-testid="journey-next-action">
                      Next: Try AGI Workhorse <Sparkles className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                ) : (
                  <Link to="/agent">
                    <Button className="w-full btn-primary" data-testid="journey-next-action">
                      Keep Going! Task Your AI Workforce <Rocket className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </motion.div>
        )}

        {/* Consumer Bookmarks Section */}
        {isConsumer && bookmarks.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-8"
          >
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-semibold text-white">Your Bookmarks</h2>
                <Link to="/bookmarks" className="text-sm text-primary hover:underline">
                  View all <ArrowRight className="inline w-4 h-4" />
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {bookmarks.slice(0, 3).map((bookmark) => (
                  <Link key={bookmark.id} to={`/directory/business/${bookmark.business_id}`}>
                    <div className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/5">
                      <h3 className="font-medium text-white truncate mb-1">{bookmark.business_name}</h3>
                      <p className="text-sm text-gray-400 truncate">{bookmark.business_category}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        <span className="text-xs text-gray-400">{bookmark.business_rating || 'N/A'}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Business users: Recent Orders Section */}
        {!isConsumer && (
        <>
          {/* Quick Actions Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
            className="mb-8"
          >
            <h2 className="font-heading text-lg font-semibold text-white mb-4">Quick Actions</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <Link to="/cinematic-ads" data-testid="quick-action-cinematic">
                <div className="glass rounded-xl p-4 hover:border-cyan-500/30 transition-all cursor-pointer group h-full">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Film className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm group-hover:text-cyan-400 transition-colors">Create Video Ad</p>
                      <p className="text-xs text-gray-500">Cinematic Studio</p>
                    </div>
                  </div>
                </div>
              </Link>
              
              <Link to="/publishing" data-testid="quick-action-publish">
                <div className="glass rounded-xl p-4 hover:border-primary/30 transition-all cursor-pointer group h-full">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Send className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm group-hover:text-primary transition-colors">Publish Content</p>
                      <p className="text-xs text-gray-500">To Directory</p>
                    </div>
                  </div>
                </div>
              </Link>
              
              <Link to="/agent" data-testid="quick-action-agent">
                <div className="glass rounded-xl p-4 hover:border-cyan-500/30 transition-all cursor-pointer group h-full">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Sparkles className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm group-hover:text-cyan-400 transition-colors">AGI Workhorse</p>
                      <p className="text-xs text-gray-500">Task Me Anything</p>
                    </div>
                  </div>
                </div>
              </Link>
              
              <Link to="/diy-tools" data-testid="quick-action-diy">
                <div className="glass rounded-xl p-4 hover:border-purple-500/30 transition-all cursor-pointer group h-full">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Wand2 className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm group-hover:text-purple-400 transition-colors">DIY Tools</p>
                      <p className="text-xs text-gray-500">30+ AI Tools</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
          {/* Recent Orders */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-semibold text-white">Recent Orders & Sessions</h2>
                <Link to="/orders" className="text-sm text-primary hover:underline">
                  View all <ArrowRight className="inline w-4 h-4" />
                </Link>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">No orders yet</p>
                  <Link to="/agent">
                    <Button className="btn-primary">Explore AGI Workhorse</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <Link 
                      key={order.id} 
                      to={order.isDWYSession ? '/expert-session' : `/orders/${order.id}`}
                      className="block"
                      data-testid={`order-card-${order.id}`}
                    >
                      <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-white truncate flex items-center gap-2">
                            {order.isDWYSession && (
                              <span className="px-2 py-0.5 bg-accent/20 text-accent text-xs rounded">DWY</span>
                            )}
                            {order.structured_data_json?.task_name || order.service_title || order.topic || 'Order'}
                          </h3>
                          <p className="text-sm text-gray-400 truncate">
                            {order.department_name} • {formatDate(order.created_at)}
                            {order.isDWYSession && order.selected_date && (
                              <span className="text-primary ml-2">• Scheduled: {order.selected_date}</span>
                            )}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                          order.isDWYSession 
                            ? order.payment_status === 'paid' 
                              ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                              : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                            : getStatusColor(order.status)
                        }`}>
                          {order.isDWYSession 
                            ? (order.payment_status === 'paid' ? 'Paid' : 'Pending')
                            : getStatusLabel(order.status)
                          }
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Athena - AI Executive Assistant */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="glass rounded-2xl overflow-hidden relative">
              {/* Athena Image with gradient fade */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_aipowered-5/artifacts/7tfm7q8x_ChatGPT%20Image%20Jan%209%2C%202026%2C%2005_02_53%20PM.png"
                  alt="Athena - AI Executive Assistant"
                  className="w-full h-64 object-cover object-top"
                  style={{
                    maskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)'
                  }}
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
              </div>
              
              {/* Content overlay */}
              <div className="relative -mt-16 px-6 pb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center border border-amber-500/30">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-white">Athena</h2>
                    <p className="text-xs text-amber-400">AI Executive Assistant</p>
                  </div>
                </div>

                <p className="text-sm text-gray-300 mb-4">
                  Your goddess of strategy and wisdom. Ask me anything or let&apos;s talk!
                </p>

                <div className="space-y-3">
                  <textarea
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    placeholder="How can I help you today?"
                    className="w-full h-20 px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white placeholder:text-gray-600 focus:border-amber-500/50 focus:outline-none resize-none text-sm"
                    data-testid="athena-chat-input"
                  />
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleQuickChat}
                      className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                      disabled={chatLoading || !chatMessage.trim()}
                      data-testid="athena-chat-submit"
                    >
                      {chatLoading ? (
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>Ask Athena</>
                      )}
                    </Button>
                    
                    <Button 
                      onClick={() => setShowVoiceChat(true)}
                      className="px-4 bg-white/10 hover:bg-white/20 text-white border border-white/20"
                      data-testid="athena-voice-btn"
                    >
                      <Phone className="w-4 h-4" />
                    </Button>
                  </div>

                  {chatResponse && (
                    <div className="p-4 rounded-xl bg-black/30 border border-amber-500/20">
                      <div 
                        className="text-sm text-gray-300 ai-response"
                        dangerouslySetInnerHTML={{ __html: formatAIResponse(chatResponse) }}
                      />
                    </div>
                  )}

                  <Link to="/knowledge" className="block text-center text-sm text-gray-400 hover:text-amber-400">
                    Open full AI chat →
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Community Analytics Widget */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <MemberAnalyticsDashboard />
          </motion.div>

          {/* Quick Community Links */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass rounded-2xl p-5"
          >
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Community
            </h3>
            <div className="space-y-3">
              <Link 
                to="/publishing" 
                className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-primary/10 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                  <Send className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-white text-sm">Publishing</p>
                  <p className="text-xs text-gray-400">Share content to directory</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-primary transition-colors" />
              </Link>
              <Link 
                to="/messages" 
                className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-cyan-500/10 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
                  <MessageCircle className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-white text-sm">Messages</p>
                  <p className="text-xs text-gray-400">Chat with members</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-cyan-400 transition-colors" />
              </Link>
              <Link 
                to="/training" 
                className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-amber-500/10 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                  <GraduationCap className="w-5 h-5 text-amber-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-white text-sm">Platform Training</p>
                  <p className="text-xs text-gray-400">Learn the platform</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-amber-400 transition-colors" />
              </Link>
              <a 
                href={EXTERNAL_DIRECTORY_URL} 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-teal-500/10 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center group-hover:bg-teal-500/30 transition-colors">
                  <Users className="w-5 h-5 text-teal-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-white text-sm">Directory</p>
                  <p className="text-xs text-gray-400">Explore community</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-teal-400 transition-colors" />
              </a>
              
              {/* External Links */}
              <div className="border-t border-white/10 pt-3 mt-3">
                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Partner Resources</p>
              </div>
              <Link 
                to="/ai-besty-link"
                className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 hover:from-amber-500/20 hover:to-orange-500/20 border border-amber-500/20 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                  <Award className="w-5 h-5 text-amber-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-amber-400 text-sm">AI Besty Affiliate Link</p>
                  <p className="text-xs text-gray-400">Earn $60 per qualified referral</p>
                </div>
                <ChevronRight className="w-4 h-4 text-amber-500/50 group-hover:text-amber-400 transition-colors" />
              </Link>
              <Link 
                to="/affiliate"
                className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-purple-500/10 to-indigo-500/10 hover:from-purple-500/20 hover:to-indigo-500/20 border border-purple-500/20 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-purple-400 text-sm">See Affiliate Reporting</p>
                  <p className="text-xs text-gray-400">Track your earnings & referrals</p>
                </div>
                <ChevronRight className="w-4 h-4 text-purple-500/50 group-hover:text-purple-400 transition-colors" />
              </Link>
              <a 
                href="https://aihorsepowered.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 hover:from-green-500/20 hover:to-emerald-500/20 border border-green-500/20 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-green-400 text-sm">$120K Business Co-Op Grant</p>
                  <p className="text-xs text-gray-400">Get qualified for funding</p>
                </div>
                <ExternalLink className="w-4 h-4 text-green-500/50 group-hover:text-green-400 transition-colors" />
              </a>
              
              {/* Gift Portal */}
              <div className="border-t border-white/10 pt-3 mt-3">
                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Client & Employee Rewards</p>
              </div>
              <Link 
                to="/gifts"
                className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 hover:from-blue-500/20 hover:to-cyan-500/20 border border-blue-500/20 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                  <Gift className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-blue-400 text-sm">Gift Portal - Resort Stays</p>
                  <p className="text-xs text-gray-400">Unlimited vacation certificates</p>
                </div>
                <ChevronRight className="w-4 h-4 text-blue-500/50 group-hover:text-blue-400 transition-colors" />
              </Link>
            </div>
          </motion.div>
        </div>
        </>
        )}

        {/* Consumer Community Section */}
        {isConsumer && (
          <div className="grid lg:grid-cols-1 gap-8">
            {/* Consumer Quick Links - Full Width */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass rounded-2xl p-6"
            >
              <h3 className="font-semibold text-white mb-6 flex items-center gap-2 text-lg">
                <Users className="w-6 h-6 text-cyan-400" />
                Community & Features
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link 
                  to="/messages" 
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-cyan-500/10 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
                    <MessageCircle className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">Messages</p>
                    <p className="text-xs text-gray-400">Chat with businesses</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-cyan-400 transition-colors" />
                </Link>
                <Link 
                  to="/bookmarks" 
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-amber-500/10 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                    <Bookmark className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">Bookmarks</p>
                    <p className="text-xs text-gray-400">Your saved businesses</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-amber-400 transition-colors" />
                </Link>
                <a 
                  href={EXTERNAL_DIRECTORY_URL} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-teal-500/10 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center group-hover:bg-teal-500/30 transition-colors">
                    <Globe className="w-5 h-5 text-teal-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">Browse Directory</p>
                    <p className="text-xs text-gray-400">Explore local businesses</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-teal-400 transition-colors" />
                </a>
                <a 
                  href={`${EXTERNAL_DIRECTORY_URL}/deals`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-emerald-500/10 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
                    <Tag className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">Deals & Coupons</p>
                    <p className="text-xs text-gray-400">Save on local services</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-emerald-400 transition-colors" />
                </a>
              </div>
            </motion.div>
          </div>
        )}
      </div>
      
      {/* Athena Voice Chat Modal */}
      <AthenaVoiceChat 
        isOpen={showVoiceChat} 
        onClose={() => setShowVoiceChat(false)} 
      />
    </DashboardLayout>
  );
}
